import { FormField, FormHelp, Icon, UnstyledButton } from '@zillow/constellation-10';
import { IconCloseCircleFilled, IconSearchFilled } from '@zillow/constellation-icons';
import { AdornedInput, Adornment, ComboboxOption } from '@zillow/constellation';
import { StyledCombobox, StyledInput } from './StyledComponents';
import { Fragment } from 'react';
import { IsVisible } from '@zg-rentals/react-use';
import { SkeletonListItem } from '../PropertyDropdown/Skeleton';

export const filterSpecialCharacters = (str: string, replaceWith = '') =>
  str.trim().replace(/[^a-zA-Z0-9- ]/g, replaceWith);

export const MAX_LENGTH_SEARCH_TEXT = 20;
export const ERROR_MESSAGE = 'Sorry, there was an error. Please try again.';
export const MAX_CHARACTERS_MESSAGE = `You've reached the maximum characters`;

type ComboboxInputProps = {
  autoCompleteBehavior: 'none' | 'automatic' | 'manual';
  focusFirstOption: boolean;
  optionFilter: null;
  options: SearchResultItem[] | [];
  onClear?(): void;
  onTagClose?(): void;
};

export type SearchResultItem = {
  id: string;
  meta: string | JSX.Element;
  value: string;
  label: string;
  listingAlias: string;
  conversationId: string;
  folder: string;
  renterName: string;
};

type SearchBarProps = {
  fetchMoreSearchResults: (searchText: string, offset: number) => void;
  hasError: boolean;
  hasMoreSearchResults: boolean;
  helperText: string;
  isDisabled: boolean;
  onBlur?: () => void;
  onChangeInputText: (inputText: string) => void;
  onFocus?: () => void;
  onSelectSearchItem: (selectedSearchResultItem: SearchResultItem) => void;
  placeholder: string;
  renderEmptyState?: () => void;
  searchResults: SearchResultItem[];
  searchText: string;
  width?: string;
} & React.HTMLAttributes<HTMLDivElement>;

export default function SearchBar({
  fetchMoreSearchResults,
  hasError,
  hasMoreSearchResults,
  helperText,
  isDisabled,
  onBlur,
  onChangeInputText,
  onFocus,
  onSelectSearchItem,
  placeholder,
  renderEmptyState = () => null,
  searchResults,
  searchText,
  width,
  ...rest
}: SearchBarProps) {
  const highlightPatternRegex = new RegExp(`${filterSpecialCharacters(searchText, '\\$&')}`, 'i');

  const handleScroll = (isLastSearchResultVisible: boolean) => {
    if (isLastSearchResultVisible && hasMoreSearchResults) {
      const offset = searchResults.length;
      fetchMoreSearchResults(searchText, offset);
    }
  };

  return (
    <FormField
      {...rest}
      error={hasError}
      description={helperText && <FormHelp>{helperText}</FormHelp>}
      css={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        flex: '1 0 0',
        alignItems: 'center',
        paddingLeft: 'default',
        paddingRight: 'default',
        maxWidth: `${width}px`,
      }}
      control={
        <StyledCombobox
          autoCompleteBehavior="manual"
          allowSubmitWhileOpen={true}
          fluidDropdown={true}
          focusFirstOption={true}
          freeForm={true}
          onChange={onChangeInputText}
          onOptionSelect={onSelectSearchItem}
          optionFilter={null}
          options={searchResults}
          value={searchText}
          /* eslint-disable-next-line @typescript-eslint/no-unused-vars */
          renderInput={({ onClear, onTagClose, ...rest }: ComboboxInputProps) => {
            return (
              <AdornedInput
                disabled={isDisabled}
                input={
                  <StyledInput
                    {...rest}
                    autoComplete="off"
                    onFocus={onFocus}
                    onBlur={onBlur}
                    placeholder={placeholder}
                    maxLength={MAX_LENGTH_SEARCH_TEXT}
                    size="sm"
                  />
                }
                rightAdornment={
                  <Adornment>
                    {searchText ? (
                      <UnstyledButton onClick={onClear} title="Clear" css={{ display: 'flex' }}>
                        <Icon css={{ color: 'icon.muted' }}>
                          <IconCloseCircleFilled />
                        </Icon>
                      </UnstyledButton>
                    ) : (
                      <Icon css={{ color: 'icon.muted' }}>
                        <IconSearchFilled />
                      </Icon>
                    )}
                  </Adornment>
                }
              />
            );
          }}
          renderOption={(props: { id: string }) => {
            const { id } = props;
            const isLastItemInList = id === searchResults[searchResults.length - 1].id;
            const showVisibilitySensor = isLastItemInList && hasMoreSearchResults;
            return (
              <Fragment key={id}>
                <ComboboxOption {...props} highlightPattern={highlightPatternRegex} />
                {showVisibilitySensor && (
                  <IsVisible onChange={handleScroll} options={{ rootMargin: '0px 0px -200px 0px' }}>
                    {({ ref }) => <SkeletonListItem ref={ref} />}
                  </IsVisible>
                )}
              </Fragment>
            );
          }}
          renderEmptyState={renderEmptyState}
          showLabelForValue={true}
          $hideDropdown={searchText.length === 0}
          $adjustDropdownHeight={searchResults.length >= 4}
        />
      }
    />
  );
}
